import { PARTNER_API_BASE, PARTNER_APPLICATION_ENDPOINT } from '../data/partners'

export class PartnerApiNotConfiguredError extends Error {}
export class PartnerApiUnauthorizedError extends Error {}

export const isVotesApiConfigured = () => PARTNER_API_BASE.trim().length > 0
export const isApplicationEndpointConfigured = () => PARTNER_APPLICATION_ENDPOINT.trim().length > 0

async function request<T>(url: string, init: RequestInit = {}, withCredentials = true): Promise<T> {
  const response = await fetch(url, {
    ...init,
    credentials: withCredentials ? 'include' : 'omit',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...init.headers },
  })
  if (response.status === 401 || response.status === 403) throw new PartnerApiUnauthorizedError('Sign-in required')
  if (!response.ok) throw new Error(`Request failed (${response.status})`)
  if (response.status === 204) return undefined as T
  const text = await response.text()
  return (text ? JSON.parse(text) : undefined) as T
}

const votesUrl = (path: string) => `${PARTNER_API_BASE.replace(/\/$/, '')}${path}`

export type VoteSummary = { authenticated: boolean; counts: Record<string, number>; viewerVotes: string[] }

export function fetchVoteSummary() {
  if (!isVotesApiConfigured()) return Promise.reject(new PartnerApiNotConfiguredError('Votes API not configured'))
  return request<VoteSummary>(votesUrl('/partners/votes'))
}

export function setPartnerVote(slug: string, voted: boolean) {
  if (!isVotesApiConfigured()) return Promise.reject(new PartnerApiNotConfiguredError('Votes API not configured'))
  return request<{ count: number; voted: boolean }>(votesUrl(`/partners/${encodeURIComponent(slug)}/vote`), {
    method: voted ? 'PUT' : 'DELETE',
  })
}

export type PartnerApplication = {
  companyName: string
  contactName: string
  workEmail: string
  website: string
  category: string
  description: string
  interest: string
  sourcePage: string
}

export function submitPartnerApplication(payload: PartnerApplication) {
  if (!isApplicationEndpointConfigured()) {
    return Promise.reject(new PartnerApiNotConfiguredError('Application endpoint not configured'))
  }
  return request<void>(PARTNER_APPLICATION_ENDPOINT, { method: 'POST', body: JSON.stringify(payload) }, false)
}
